import { APP_ROLES, type AppRole } from '@tabel/shared/schemas/auth';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Шесть ролей 002 (contracts/http-api.md, «Общие правила»; spec.md, «Права ролей») — из общей схемы, как роли
 * ответа `/api/me` (004, research R18). Роли 001 (`brigadier`, `hr`) — их часть. Роль назначается на объект, пару
 * проверяет сервер.
 */
export { APP_ROLES, type AppRole };

export const ROLE_LABEL: Record<AppRole, string> = {
  brigadier: 'Бригадир',
  hr: 'Кадры',
  rules_admin: 'Администратор правил',
  approver: 'Утверждающий',
  auditor: 'Проверяющий',
  sysadmin: 'Администратор системы',
};

export function isAppRole(v: unknown): v is AppRole {
  return typeof v === 'string' && (APP_ROLES as readonly string[]).includes(v);
}

/**
 * Домашняя страница роли (004, research R18; contracts/ui.md, «Оболочка»): бригадир — «Табели», кадры — «На
 * проверку», остальные роли — «Правила расчёта». Адреса — те же, что `NAV_PATH` меню (`lib/nav.ts` импортирует этот
 * модуль, поэтому здесь они строками; что это пункт меню роли, проверяет `nav.test.tsx`).
 */
const HOME: Readonly<Record<AppRole, string>> = {
  brigadier: '/brigadier',
  hr: '/hr',
  rules_admin: '/rules',
  approver: '/rules',
  auditor: '/rules',
  sysadmin: '/rules',
};

export function homeOf(role: AppRole): string {
  return HOME[role];
}

/** Идентификатор объекта (`objects.id`, например `sakh`): только символы, допустимые в заголовке. */
const OBJECT_ID = /^[A-Za-z0-9_.:-]{1,64}$/;
export function isObjectId(v: unknown): v is string {
  return typeof v === 'string' && OBJECT_ID.test(v);
}

const ROLE_KEY = 'tabel.role';
const OBJECT_KEY = 'tabel.object';

interface Selection {
  role: AppRole;
  objectId: string | null;
}

/** Выбор по умолчанию: бригадир без объекта. */
const DEFAULT_SELECTION: Selection = { role: 'brigadier', objectId: null };

let current: Selection = readStored();

function readStored(): Selection {
  try {
    const role = localStorage.getItem(ROLE_KEY);
    const objectId = localStorage.getItem(OBJECT_KEY);
    return {
      role: isAppRole(role) ? role : DEFAULT_SELECTION.role,
      objectId: isObjectId(objectId) ? objectId : null,
    };
  } catch {
    return { ...DEFAULT_SELECTION };
  }
}

function store(s: Selection): void {
  try {
    localStorage.setItem(ROLE_KEY, s.role);
    if (s.objectId) localStorage.setItem(OBJECT_KEY, s.objectId);
    else localStorage.removeItem(OBJECT_KEY);
  } catch {
    // хранилище недоступно — выбор живёт до перезагрузки
  }
}

function forget(): void {
  try {
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(OBJECT_KEY);
  } catch {
    // хранилище недоступно — забывать нечего
  }
}

/** Текущая роль для заголовка X-Role — в каждом запросе (FR-002). */
export function getRole(): AppRole {
  return current.role;
}

/** Выбранный объект для заголовка X-Object; `null` — не выбран. */
export function getObjectId(): string | null {
  return current.objectId;
}

/**
 * Заголовки роли и объекта для каждого запроса с сессией.
 * `X-Object` — только когда объект выбран: при одном объекте у пользователя его можно не передавать.
 */
export function contextHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'X-Role': current.role };
  if (current.objectId) h['X-Object'] = current.objectId;
  return h;
}

interface RoleContextValue extends Selection {
  /** Сменить роль; с `objectId` — сразу пару роль + объект (`null` — без объекта). */
  setRole: (role: AppRole, objectId?: string | null) => void;
  setObject: (objectId: string | null) => void;
  /**
   * Забыть выбор при выходе: роль по умолчанию, без объекта, хранилище очищено, кэш запросов пуст.
   * Следующий пользователь того же браузера не получает чужой X-Object.
   */
  clearSelection: () => void;
}

const RoleContext = createContext<RoleContextValue>({
  ...current,
  setRole: () => {},
  setObject: () => {},
  clearSelection: () => {},
});

/**
 * Роль и объект интерфейса. Ключи запросов не содержат объекта, поэтому при смене объекта кэш
 * сбрасывается: на экране не остаются табели и списки прежнего объекта, активные запросы
 * перезапрашиваются уже с новым X-Object. Должен стоять внутри `QueryClientProvider`.
 */
export function RoleProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [sel, setSel] = useState<Selection>(current);
  const apply = useCallback(
    (next: Selection) => {
      const objectChanged = next.objectId !== current.objectId;
      current = next;
      store(next);
      setSel(next);
      if (objectChanged) void queryClient.resetQueries();
    },
    [queryClient],
  );
  const setRole = useCallback(
    (role: AppRole, objectId?: string | null) =>
      apply({
        role,
        objectId: objectId === undefined ? current.objectId : isObjectId(objectId) ? objectId : null,
      }),
    [apply],
  );
  const setObject = useCallback(
    (objectId: string | null) =>
      apply({ role: current.role, objectId: isObjectId(objectId) ? objectId : null }),
    [apply],
  );
  const clearSelection = useCallback(() => {
    current = { ...DEFAULT_SELECTION };
    forget();
    setSel(current);
    // Без перезапроса: сессии уже нет, активные запросы получили бы отказ.
    queryClient.clear();
  }, [queryClient]);
  const value = useMemo(
    () => ({ ...sel, setRole, setObject, clearSelection }),
    [sel, setRole, setObject, clearSelection],
  );
  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  return useContext(RoleContext);
}
